import React, { useEffect, useState } from "react";
import axios from "axios";
import { Stethoscope, Plus, Edit, Trash2, Phone, Mail, MapPin } from "lucide-react";

import { useNotification } from "../contexts/NotificationContext";
import { getApiUrl } from "../config/api";

export default function Veterinarians() {
  const { showSuccess, showError, showWarning } = useNotification();
  const [veterinarians, setVeterinarians] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [form, setForm] = useState({
    name: "",
    clinic_name: "",
    phone: "",
    email: "",
    address: "",
    specialties: "",
    notes: ""
  });

  const [editData, setEditData] = useState({});

  // Delete confirmation state
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deletingVet, setDeletingVet] = useState(null);

  useEffect(() => {
    fetchVeterinarians();
  }, []);

  const fetchVeterinarians = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const res = await axios.get(getApiUrl("/api/veterinarians"), {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });
      setVeterinarians(res.data);
    } catch (error) {
      console.error("Error fetching veterinarians:", error);
      setError("Failed to load veterinarians");
      showError("Failed to load veterinarians");
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm({ ...form, [name]: value });
  };

  const handleSubmit = async () => {
    // Validate required fields
    if (!form.name.trim()) {
      showWarning("Veterinarian name is required");
      return;
    }

    try {
      await axios.post(getApiUrl("/api/veterinarians"), form, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });
      setForm({
        name: "",
        clinic_name: "",
        phone: "",
        email: "",
        address: "",
        specialties: "",
        notes: ""
      });
      setShowModal(false);
      showSuccess("Veterinarian added successfully!");
      fetchVeterinarians();
    } catch (err) {
      console.error("Submit error:", err);
      if (err.response?.data?.error) {
        showError(err.response.data.error);
      } else {
        showError("Failed to add veterinarian");
      }
    }
  };

  const openAddModal = () => {
    setEditingId(null);
    setForm({
      name: "",
      clinic_name: "",
      phone: "",
      email: "",
      address: "",
      specialties: "",
      notes: ""
    });
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingId(null);
    setForm({
      name: "",
      clinic_name: "",
      phone: "",
      email: "",
      address: "",
      specialties: "",
      notes: ""
    });
  };

  const startEdit = (vet) => {
    setEditingId(vet.id);
    setEditData({
      name: vet.name || "",
      clinic_name: vet.clinic_name || "",
      phone: vet.phone || "",
      email: vet.email || "",
      address: vet.address || "",
      specialties: vet.specialties || "",
      notes: vet.notes || ""
    });
  };

  const saveEdit = async (id) => {
    // Validate required fields
    if (!editData.name?.trim()) {
      showWarning("Veterinarian name is required");
      return;
    }

    try {
      await axios.put(getApiUrl(`/api/veterinarians/${id}`), editData, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });
      setEditingId(null);
      showSuccess("Veterinarian updated successfully!");
      fetchVeterinarians();
    } catch (err) {
      console.error("Update error:", err);
      if (err.response?.data?.error) {
        showError(err.response.data.error);
      } else {
        showError("Failed to update veterinarian");
      }
    }
  };

  const cancelEdit = () => setEditingId(null);

  const handleDelete = async (id) => {
    setDeletingVet(id);
    setShowDeleteConfirm(true);
  };

  const confirmDelete = async () => {
    if (!deletingVet) return;
    try {
      await axios.delete(getApiUrl(`/api/veterinarians/${deletingVet}`), {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });
      showSuccess("Veterinarian deleted successfully!");
      fetchVeterinarians();
    } catch (err) {
      console.error("Delete error:", err);
      showError("Failed to delete veterinarian");
    } finally {
      setDeletingVet(null);
      setShowDeleteConfirm(false);
    }
  };

  const cancelDelete = () => {
    setDeletingVet(null);
    setShowDeleteConfirm(false);
  };

  // Add error boundary
  if (error) {
    return (
      <div className="p-6 max-w-4xl mx-auto">
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <Stethoscope className="h-8 w-8 text-black" />
          Veterinarians
        </h1>
        <div className="bg-red-50 border border-red-200 rounded p-4">
          <p className="text-red-600">Error: {error}</p>
          <button 
            onClick={fetchVeterinarians}
            className="mt-2 bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mb-6 gap-4">
        <h1 className="text-xl sm:text-2xl font-bold text-gray-900 flex items-center">
          <Stethoscope className="mr-2 h-6 w-6 sm:h-8 sm:w-8 text-black" />
          Veterinarians
        </h1>
        <button
          onClick={openAddModal}
          className="bg-[#51380d] text-white px-4 sm:px-6 py-2 sm:py-2 rounded hover:bg-[#3d2a0a] whitespace-nowrap w-full sm:w-auto flex items-center justify-center"
        >
          <Plus className="mr-2 h-4 w-4" />
          <span className="hidden sm:inline">Add Veterinarian</span>
          <span className="sm:hidden">Add Veterinarian</span>
        </button>
      </div>



      <div className="bg-white shadow p-6 rounded">
        <h3 className="text-lg font-semibold mb-4">Veterinarian List</h3>
        {loading ? (
          <p className="text-gray-500">Loading veterinarians...</p>
        ) : veterinarians.length === 0 ? (
          <div className="text-center py-8">
            <h3 className="text-lg font-medium text-gray-900 mb-2">No veterinarians yet</h3>
            <p className="text-gray-500 mb-4">Get started by adding your first veterinarian</p>
            <button
              onClick={openAddModal}
              className="bg-[#51380d] text-white px-4 py-2 rounded hover:bg-[#3d2a0a]"
            >
              Add Your First Veterinarian
            </button>
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {veterinarians.map((vet) => (
              <div key={vet.id} className="p-6">
                {editingId === vet.id ? (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
                      <input
                        className="border px-3 py-3 sm:px-2 sm:py-1 rounded w-full text-base sm:text-sm"
                        name="name"
                        value={editData.name}
                        onChange={(e) => setEditData({ ...editData, name: e.target.value })}
                        placeholder="e.g., Dr. Sarah Johnson"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Clinic Name</label>
                      <input
                        className="border px-3 py-3 sm:px-2 sm:py-1 rounded w-full text-base sm:text-sm"
                        name="clinic_name"
                        value={editData.clinic_name}
                        onChange={(e) => setEditData({ ...editData, clinic_name: e.target.value })}
                        placeholder="e.g., ABC Veterinary Clinic"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                      <input
                        className="border px-3 py-3 sm:px-2 sm:py-1 rounded w-full text-base sm:text-sm"
                        name="phone"
                        value={editData.phone}
                        onChange={(e) => setEditData({ ...editData, phone: e.target.value })}
                        placeholder="e.g., (555) 123-4567"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                      <input
                        className="border px-3 py-3 sm:px-2 sm:py-1 rounded w-full text-base sm:text-sm"
                        name="email"
                        value={editData.email}
                        onChange={(e) => setEditData({ ...editData, email: e.target.value })}
                        placeholder="e.g., dr.johnson@clinic.com"
                      />
                    </div>
                    <div className="sm:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-1">Address</label>
                      <input
                        className="border px-3 py-3 sm:px-2 sm:py-1 rounded w-full text-base sm:text-sm"
                        name="address"
                        value={editData.address}
                        onChange={(e) => setEditData({ ...editData, address: e.target.value })}
                        placeholder="e.g., 123 Main St, City, State 12345"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Specialties</label>
                      <input
                        className="border px-3 py-3 sm:px-2 sm:py-1 rounded w-full text-base sm:text-sm"
                        name="specialties"
                        value={editData.specialties}
                        onChange={(e) => setEditData({ ...editData, specialties: e.target.value })}
                        placeholder="e.g., Equine Surgery, Emergency Care"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                      <input
                        className="border px-3 py-3 sm:px-2 sm:py-1 rounded w-full text-base sm:text-sm"
                        name="notes"
                        value={editData.notes}
                        onChange={(e) => setEditData({ ...editData, notes: e.target.value })}
                        placeholder="Any additional information"
                      />
                    </div>
                    <div className="sm:col-span-2 flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-2">
                      <button
                        onClick={() => saveEdit(vet.id)}
                        className="w-full sm:w-auto bg-green-600 text-white px-4 py-3 sm:py-2 rounded hover:bg-green-700 text-base sm:text-sm"
                      >
                        Save
                      </button>
                      <button
                        onClick={cancelEdit}
                        className="w-full sm:w-auto bg-gray-600 text-white px-4 py-3 sm:py-2 rounded hover:bg-gray-700 text-base sm:text-sm"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start">
                    <div className="flex-1 mb-4 sm:mb-0">
                      <h3 className="text-lg font-semibold text-gray-900 mb-2">{vet.name}</h3>
                      <div className="space-y-1 text-sm text-gray-600">
                        {vet.clinic_name && (
                          <div className="flex items-center">
                            <Stethoscope className="w-4 h-4 mr-2 flex-shrink-0" />
                            <span className="break-words">{vet.clinic_name}</span>
                          </div>
                        )}
                        {vet.phone && (
                          <div className="flex items-center">
                            <Phone className="w-4 h-4 mr-2 flex-shrink-0" />
                            <span className="break-words">{vet.phone}</span>
                          </div>
                        )}
                        {vet.email && (
                          <div className="flex items-center">
                            <Mail className="w-4 h-4 mr-2 flex-shrink-0" />
                            <span className="break-words">{vet.email}</span>
                          </div>
                        )}
                        {vet.address && (
                          <div className="flex items-start">
                            <MapPin className="w-4 h-4 mr-2 mt-0.5 flex-shrink-0" />
                            <span className="break-words">{vet.address}</span>
                          </div>
                        )}
                        {vet.specialties && (
                          <div className="flex items-start">
                            <MapPin className="w-4 h-4 mr-2 mt-0.5 flex-shrink-0" />
                            <span className="break-words">{vet.specialties}</span>
                          </div>
                        )}
                        {vet.notes && (
                          <div className="flex items-start">
                            <MapPin className="w-4 h-4 mr-2 mt-0.5 flex-shrink-0" />
                            <span className="break-words">{vet.notes}</span>
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="flex space-x-2 sm:ml-4">
                      {/* Mobile mode: icon-only buttons */}
                      <div className="sm:hidden flex space-x-2">
                        <button
                          onClick={() => startEdit(vet)}
                          className="p-3 rounded text-blue-600 hover:text-blue-800 hover:bg-blue-50 border border-blue-200"
                          aria-label="Edit"
                          title="Edit"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(vet.id)}
                          className="p-3 rounded text-red-600 hover:text-red-800 hover:bg-red-50 border border-red-200"
                          aria-label="Delete"
                          title="Delete"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                      
                      {/* Desktop mode: text buttons */}
                      <div className="hidden sm:flex space-x-2">
                        <button
                          onClick={() => startEdit(vet)}
                          className="px-2 py-1 rounded text-xs text-blue-600 hover:text-blue-900 hover:bg-blue-50 border border-blue-200"
                          title="Edit"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleDelete(vet.id)}
                          className="px-2 py-1 rounded text-xs text-red-600 hover:text-red-900 hover:bg-red-50 border border-red-200"
                          title="Delete"
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-[70] flex items-start sm:items-center justify-center p-4 pt-20 sm:pt-4">
          <div className="relative mx-auto p-4 sm:p-5 border w-full max-w-md shadow-lg rounded-md bg-white mt-4 sm:mt-0">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Add New Veterinarian
              </h3>
              
              <form onSubmit={(e) => { e.preventDefault(); handleSubmit(); }} className="space-y-4 sm:space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Name *
                  </label>
                  <input
                    name="name"
                    value={form.name}
                    onChange={handleChange}
                    placeholder="e.g., Dr. Sarah Johnson"
                    className="border px-3 py-3 sm:py-2 rounded w-full text-base sm:text-sm"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Clinic Name
                  </label>
                  <input
                    name="clinic_name"
                    value={form.clinic_name}
                    onChange={handleChange}
                    placeholder="e.g., ABC Veterinary Clinic"
                    className="border px-3 py-3 sm:py-2 rounded w-full text-base sm:text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Phone
                  </label>
                  <input
                    name="phone"
                    value={form.phone}
                    onChange={handleChange}
                    placeholder="e.g., (555) 123-4567"
                    className="border px-3 py-3 sm:py-2 rounded w-full text-base sm:text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Email
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={form.email}
                    onChange={handleChange}
                    placeholder="e.g., dr.johnson@clinic.com"
                    className="border px-3 py-3 sm:py-2 rounded w-full text-base sm:text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Address
                  </label>
                  <input
                    name="address"
                    value={form.address}
                    onChange={handleChange}
                    placeholder="e.g., 123 Main St, City, State 12345"
                    className="border px-3 py-3 sm:py-2 rounded w-full text-base sm:text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Specialties
                  </label>
                  <input
                    name="specialties"
                    value={form.specialties}
                    onChange={handleChange}
                    placeholder="e.g., Equine Surgery, Emergency Care"
                    className="border px-3 py-3 sm:py-2 rounded w-full text-base sm:text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Notes
                  </label>
                  <input
                    name="notes"
                    value={form.notes}
                    onChange={handleChange}
                    placeholder="Any additional information"
                    className="border px-3 py-3 sm:py-2 rounded w-full text-base sm:text-sm"
                  />
                </div>
                <div className="flex flex-col sm:flex-row justify-center space-y-2 sm:space-y-0 sm:space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={closeModal}
                    className="w-full sm:w-auto px-4 py-3 sm:py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 transition-colors text-base sm:text-sm"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="w-full sm:w-auto px-4 py-3 sm:py-2 bg-[#51380d] text-white rounded-md hover:bg-[#3d2a0a] transition-colors text-base sm:text-sm"
                  >
                    Add Veterinarian
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full flex items-start sm:items-center justify-center z-[70] p-4 pt-20 sm:pt-4">
          <div className="relative p-6 sm:p-8 border w-full max-w-md shadow-lg rounded-md bg-white mt-4 sm:mt-0">
            <div className="text-center">
              <Trash2 className="mx-auto h-12 w-12 text-red-500" />
              <h3 className="text-lg leading-6 font-medium text-gray-900">
                Are you sure?
              </h3>
              <div className="mt-2 px-7 py-3">
                <p className="text-sm text-gray-500">
                  This action cannot be undone.
                </p>
              </div>
              <div className="items-center px-4 py-3 space-y-3">
                <button
                  onClick={confirmDelete}
                  className="px-4 py-3 sm:py-2 bg-red-600 text-white text-base font-medium rounded-md w-full shadow-sm hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500"
                >
                  Delete
                </button>
                <button
                  onClick={cancelDelete}
                  className="px-4 py-3 sm:py-2 bg-gray-200 text-gray-800 text-base font-medium rounded-md w-full shadow-sm hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-500"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
} 